/**
 * Shared, dependency-free Target Investor Progress contract.
 * Keep this module CommonJS so frontend, Functions, and Node scripts use one runtime source.
 */

const CANONICAL_PROGRESS_OPTIONS = Object.freeze([
  { value: 'not_contacted', label: 'No Contact' },
  { value: 'outreach_sent', label: 'Sent' },
  { value: 'contact_established', label: 'Contact' },
  { value: 'information_requested', label: 'Info Request' },
  { value: 'initial_interest', label: 'Interest' },
  { value: 'meeting_scheduled', label: 'Meet Booked' },
  { value: 'meeting_completed', label: 'Meet Complete' },
  { value: 'under_review', label: 'In Review' },
  { value: 'due_diligence', label: 'In DD' },
  { value: 'terms_discussion', label: 'Terms' },
  { value: 'committed', label: 'Committed' },
  { value: 'invested', label: 'Invested' },
  { value: 'passed', label: 'Passed' },
  { value: 'unresponsive', label: 'Gone Cold' },
  { value: 'on_hold', label: 'On Hold' },
]);

const LEGACY_PROGRESS_OPTIONS = Object.freeze([
  { value: 'not_sent', label: 'Not Sent' },
  { value: 'linkedin_request', label: 'LinkedIn Request' },
  { value: 'email_sent', label: 'Email Sent' },
  { value: 'more_info', label: 'More Info' },
  { value: 'interested', label: 'Interested' },
  { value: 'in_play', label: 'In Play' },
  { value: 'investing', label: 'Investing' },
]);

const DETERMINISTIC_MIGRATION_MAP = Object.freeze({
  not_sent: 'not_contacted',
  linkedin_request: 'outreach_sent',
  email_sent: 'outreach_sent',
  more_info: 'information_requested',
  interested: 'initial_interest',
});

const CANONICAL_LABELS = Object.freeze(
  Object.fromEntries(CANONICAL_PROGRESS_OPTIONS.map(({ value, label }) => [value, label]))
);
const LEGACY_LABELS = Object.freeze(
  Object.fromEntries(LEGACY_PROGRESS_OPTIONS.map(({ value, label }) => [value, label]))
);
const CANONICAL_VALUES = Object.freeze(CANONICAL_PROGRESS_OPTIONS.map(({ value }) => value));
const LEGACY_VALUES = Object.freeze(LEGACY_PROGRESS_OPTIONS.map(({ value }) => value));
const CANONICAL_SET = new Set(CANONICAL_VALUES);
const LEGACY_SET = new Set(LEGACY_VALUES);

const safeRawValue = (raw) => {
  if (typeof raw === 'string') {
    const normalized = raw.trim();
    return (normalized || '(empty string)').slice(0, 120);
  }
  if (raw === null) return 'null';
  if (raw === undefined) return 'undefined';
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  return Object.prototype.toString.call(raw).slice(0, 120);
};

const isCanonicalProgress = (raw) => typeof raw === 'string' && CANONICAL_SET.has(raw);
const isLegacyProgress = (raw) => typeof raw === 'string' && LEGACY_SET.has(raw);

const classifyProgress = (raw) => {
  if (raw === null || raw === undefined || raw === '') {
    return { kind: 'missing', value: null, label: 'Not set', raw };
  }
  if (isCanonicalProgress(raw)) {
    return { kind: 'canonical', value: raw, label: CANONICAL_LABELS[raw], raw };
  }
  if (isLegacyProgress(raw)) {
    return { kind: 'legacy', value: raw, label: LEGACY_LABELS[raw], raw };
  }
  return {
    kind: 'unknown',
    value: null,
    label: `Unknown: ${safeRawValue(raw)}`,
    raw,
  };
};

const formatProgress = (raw) => {
  const classification = classifyProgress(raw);
  if (classification.kind === 'legacy') {
    return `${classification.label} (legacy — review required)`;
  }
  return classification.label;
};

const progressDataState = (raw) => {
  const { kind } = classifyProgress(raw);
  return `${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
};

const progressFilterKey = (raw) => {
  const classification = classifyProgress(raw);
  if (classification.kind === 'missing') return '__progress_missing__';
  if (classification.kind === 'unknown') {
    return `__progress_unknown__:${safeRawValue(raw)}`;
  }
  return classification.value;
};

const resolveEmailProgress = (raw) => {
  const advances =
    raw === 'not_contacted' ||
    raw === 'not_sent' ||
    raw === 'linkedin_request' ||
    raw === 'email_sent';
  return {
    value: advances ? 'outreach_sent' : raw,
    changed: advances && raw !== 'outreach_sent',
    previousValue: raw,
  };
};

const resolveLegacyEmailProgress = (raw) => {
  const advances = raw === 'not_sent' || raw === 'linkedin_request';
  return {
    value: advances ? 'email_sent' : raw,
    changed: advances,
    previousValue: raw,
  };
};

const resolveSyndicationProgressShape = (data) => {
  const flatPresent = Object.prototype.hasOwnProperty.call(data || {}, 'outreachStatus');
  const nestedPresent = Object.prototype.hasOwnProperty.call(data?.target || {}, 'outreachStatus');
  const flatValue = flatPresent ? data.outreachStatus : undefined;
  const nestedValue = nestedPresent ? data.target.outreachStatus : undefined;
  return {
    kind:
      flatPresent && nestedPresent
        ? flatValue === nestedValue
          ? 'dual_equal'
          : 'dual_conflict'
        : flatPresent
          ? 'flat'
          : nestedPresent
            ? 'nested'
            : 'missing',
    value: flatPresent ? flatValue : nestedValue,
    flatPresent,
    nestedPresent,
    flatValue,
    nestedValue,
  };
};

const mapStage4ToProgress = (raw) => {
  switch (raw) {
    case 'contacted':
      return 'outreach_sent';
    case 'responded':
      return 'contact_established';
    case 'meeting':
      return 'meeting_scheduled';
    case 'not_contacted':
    default:
      return 'not_contacted';
  }
};

const isCommittedProgress = (raw) =>
  raw === 'committed' || raw === 'invested' || raw === 'investing';
const isVerbalProgress = (raw) => raw === 'terms_discussion' || raw === 'in_play';
const isPassedProgress = (raw) => raw === 'passed';
const isPausedProgress = (raw) => raw === 'on_hold' || raw === 'unresponsive';
const isOutreachStartedProgress = (raw) =>
  isCanonicalProgress(raw) ? raw !== 'not_contacted' : isLegacyProgress(raw) && raw !== 'not_sent';

/** Info Request through Invested — Jason Active / engagement bucket (R1). */
const ENGAGED_PROGRESS_VALUES = new Set([
  'information_requested',
  'initial_interest',
  'meeting_scheduled',
  'meeting_completed',
  'under_review',
  'due_diligence',
  'terms_discussion',
  'committed',
  'invested',
]);

/** No Contact, Sent, Contact — pre-engagement; Inactive with passed/paused (R1). */
const PRE_ENGAGEMENT_PROGRESS_VALUES = new Set([
  'not_contacted',
  'outreach_sent',
  'contact_established',
]);

const isEngagedProgress = (raw) =>
  typeof raw === 'string' && ENGAGED_PROGRESS_VALUES.has(raw);
const isPreEngagementProgress = (raw) =>
  typeof raw === 'string' && PRE_ENGAGEMENT_PROGRESS_VALUES.has(raw);
/** Product Active = engaged (Info Request → Invested). Not the old 12-status funnel. */
const isActiveProgress = (raw) => isEngagedProgress(raw);

const planProgressMigration = (raw) => {
  const classification = classifyProgress(raw);
  if (classification.kind === 'canonical') {
    return { action: 'preserve', from: raw, to: raw, requiresReview: false };
  }
  if (
    classification.kind === 'legacy' &&
    Object.prototype.hasOwnProperty.call(DETERMINISTIC_MIGRATION_MAP, raw)
  ) {
    return {
      action: 'map',
      from: raw,
      to: DETERMINISTIC_MIGRATION_MAP[raw],
      requiresReview: false,
    };
  }
  return {
    action: 'review',
    from: raw,
    to: null,
    requiresReview: true,
    reason: classification.kind,
  };
};

module.exports = {
  CANONICAL_PROGRESS_OPTIONS,
  CANONICAL_PROGRESS_VALUES: CANONICAL_VALUES,
  LEGACY_PROGRESS_OPTIONS,
  LEGACY_PROGRESS_VALUES: LEGACY_VALUES,
  DETERMINISTIC_MIGRATION_MAP,
  classifyProgress,
  formatProgress,
  progressDataState,
  progressFilterKey,
  isCanonicalProgress,
  isLegacyProgress,
  resolveEmailProgress,
  resolveLegacyEmailProgress,
  resolveSyndicationProgressShape,
  mapStage4ToProgress,
  isCommittedProgress,
  isVerbalProgress,
  isEngagedProgress,
  isPreEngagementProgress,
  isActiveProgress,
  isPausedProgress,
  isPassedProgress,
  isOutreachStartedProgress,
  planProgressMigration,
};
